import type { FileNode } from '@avila-roffe/components-web';
import type { SkillFile } from './types';

interface FolderNode {
  name: string;
  path: string;
  folders: Map<string, FolderNode>;
  files: SkillFile[];
}

export function parentPath(path: string): string {
  const index = path.lastIndexOf('/');
  return index < 0 ? '' : path.slice(0, index);
}

export function childPath(parent: string, name: string): string {
  return parent ? `${parent}/${name}` : name;
}

export function renamedPath(oldPath: string, newName: string): string {
  const par = parentPath(oldPath);
  return par ? `${par}/${newName}` : newName;
}

export function collectFolderPaths(files: SkillFile[]): Set<string> {
  const paths = new Set<string>();
  for (const file of files) {
    const parts = file.path.split('/');
    if (parts.length <= 1) continue;
    let current = '';
    for (const part of parts.slice(0, -1)) {
      current = current ? `${current}/${part}` : part;
      paths.add(current);
    }
  }
  return paths;
}

function ensureFolder(root: FolderNode, folderPath: string): FolderNode {
  const parts = folderPath.split('/').filter(Boolean);
  let current = root;
  let currentPath = '';
  for (const part of parts) {
    currentPath = currentPath ? `${currentPath}/${part}` : part;
    const existing = current.folders.get(part);
    if (existing) {
      current = existing;
      continue;
    }
    const created: FolderNode = { name: part, path: currentPath, folders: new Map(), files: [] };
    current.folders.set(part, created);
    current = created;
  }
  return current;
}

export function buildFileNodes(
  files: SkillFile[],
  folders: Set<string>,
  activePath: string | null,
): FileNode[] {
  const root: FolderNode = { name: '', path: '', folders: new Map(), files: [] };
  for (const folder of folders) ensureFolder(root, folder);
  for (const file of files) {
    const par = parentPath(file.path);
    if (!par) root.files.push(file);
    else ensureFolder(root, par).files.push(file);
  }

  const byName = (a: { name: string }, b: { name: string }) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

  const toNodes = (folder: FolderNode): FileNode[] => {
    const folderNodes: FileNode[] = Array.from(folder.folders.values())
      .sort(byName)
      .map((cf) => {
        const isActive = Boolean(activePath?.startsWith(`${cf.path}/`));
        return {
          type: 'folder' as const,
          name: cf.name,
          path: cf.path,
          open: isActive,
          active: isActive,
          children: toNodes(cf),
        };
      });
    const fileNodes: FileNode[] = folder.files
      .slice()
      .sort((a, b) => byName({ name: a.path }, { name: b.path }))
      .map((file) => ({
        type: 'file' as const,
        name: file.path.split('/').pop() ?? file.path,
        path: file.path,
        active: file.path === activePath,
      }));
    return [...folderNodes, ...fileNodes];
  };

  return toNodes(root);
}

export function upsertLocalFile(files: SkillFile[], nextFile: SkillFile): SkillFile[] {
  const index = files.findIndex((f) => f.path === nextFile.path);
  if (index === -1) return [...files, nextFile];
  const next = files.slice();
  next[index] = nextFile;
  return next;
}
